import { fileHref } from '../lib/fileAccess'
import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import DateField from './DateField'
import { fetchRows, fetchArchived, archiveRow, unarchiveRow, archiveReady, createRow, updateRow, deleteRow, dbTableOf } from '../lib/api'
import { isArchived, archivedLine } from '../lib/archive'
import HistoryLink from './HistoryLink'
import Modal from './Modal'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { statusReasonAsk, reportedByDefault, statusReasonFields } from '../lib/equipmentStatus'
import { filePath } from '../lib/filePaths'
import { expiryStatus, expiryToday } from '../lib/expiry'
import { formBody } from '../lib/formBody'
import { STOCK_REASONS, STOCK_REASON_LABEL, negativeStockMessage, fmtQty } from '../lib/stockCore'
import { setRecordContext, clearRecordContext } from '../lib/appContext'
import { deleteWithUndo } from '../lib/undoDelete'
import ExportMenu from './ExportMenu'
import LinkedManuals from './LinkedManuals'
import { getVal, recordName } from '../lib/tableText'

const PILL_MAP = {
  'Operational':'pill-g','Due service':'pill-a','Out of Service':'pill-r',
  'Overdue':'pill-r','Due':'pill-a','Completed':'pill-g','Upcoming':'pill-b',
  'OK':'pill-g','Low Stock':'pill-a','Out of Stock':'pill-r','On Order':'pill-b',
  'Valid':'pill-g','Expired Soon':'pill-a','Expiring soon':'pill-a','Expired':'pill-r','Archived':'pill-x',
  'Yes':'pill-r','No':'pill-g',
  'Active':'pill-g','Confirmed':'pill-b','Option':'pill-a','Inquiry':'pill-a','Cancelled':'pill-r',
  'Paid':'pill-g','Pending':'pill-a',
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function fmtDate(v) {
  if (typeof v !== 'string') return v
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return v
  const day = parseInt(m[3], 10)
  const mon = MONTHS[parseInt(m[2], 10) - 1]
  return day + '-' + mon + '-' + m[1]
}

function fmtNum(v) {
  if (v === null || v === undefined || v === '' || v === '-') return v
  const n = Number(v)
  if (!isFinite(n)) return v
  return Math.round(n).toLocaleString('en-US')
}

function calcStatus(row, tableKey) {
  if (tableKey === 'mtask') {
    // status is already combined (date + hours) when the tasks load - pills and chips agree
    const st = getVal(row['Status']); const stv = typeof st === 'object' ? st?.value : st
    if (stv) return stv
    const due = getVal(row['Next Service Date'])
    if (!due || due === '-' || due === '') return null
    const d = new Date(due)
    const now = new Date()
    const diff = (d - now) / (1000 * 60 * 60 * 24)
    if (diff < 0) return 'Overdue'
    if (diff <= 30) return 'Due'
    if (diff <= 60) return 'Upcoming'
    const stored = getVal(row['Status'])
    const storedVal = typeof stored === 'object' ? stored.value : stored
    return storedVal || 'Upcoming'
  }
  if (!['docs','crew_docs'].includes(tableKey)) return null
  const expiry = getVal(row['Expiry Date'])
  if (!expiry || expiry === '-' || expiry === '') return 'Valid'
  const s = expiryStatus(expiry, expiryToday())   // the shared expiry rule (src/lib/expiry.js)
  return s.expired ? 'Expired' : s.soon ? 'Expiring Soon' : 'Valid'
}

const STATUS_FILTERS = {
  equipment: ['Operational','Out of Service'],
  mtask:     ['Overdue','Due','Upcoming','Completed'],
  inventory: ['OK','Low Stock','Out of Stock','On Order'],
  docs:      ['Valid','Expiring soon','Expired','Archived'],
  mlog:      [],
}

const FILTERABLE_COLS = {
  equipment: ['Department','System','Status'],
  mtask:     ['Status'],
  inventory: ['Category','Status'],
  docs:      ['Document Type','Status'],
  mlog:      [],
}

const compInp = {padding:'8px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--sea)',color:'var(--text)',fontSize:13,width:'100%',boxSizing:'border-box'}

function CommentBubble({ text }) {
  const [popover, setPopover] = useState(null)
  const btnRef = useRef(null)
  if (!text || text === '-') return <span style={{color:'var(--muted)'}}>-</span>
  function handleClick(e) {
    e.stopPropagation()
    if (popover) { setPopover(null); return }
    const rect = btnRef.current.getBoundingClientRect()
    // anchor the popover to the clicked icon, clamped inside the viewport
    const PW = 260, PH = 150
    let left = rect.left - PW - 8            // prefer left of the icon
    if (left < 8) left = Math.min(window.innerWidth - PW - 8, rect.right + 8)  // else right of it
    let top = rect.bottom + 6
    if (top + PH > window.innerHeight) top = Math.max(8, rect.top - PH - 6)
    setPopover({ left, top })
  }
  return (
    <>
      <button ref={btnRef} style={cs.commentBtn} onClick={handleClick} title="View comment">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H4l-3 3V2z"
            stroke="currentColor" strokeWidth="1.2" fill="none"/>
        </svg>
      </button>
      {popover && createPortal(
        <>
          <div style={{position:'fixed',inset:0,zIndex:9998}} onClick={() => setPopover(null)} />
          <div style={{
            position:'fixed', left:popover.left, top:popover.top, zIndex:9999,
            width:260, background:'var(--ink2)', border:'0.5px solid var(--border2)',
            borderRadius:10, padding:'12px 14px', boxShadow:'0 8px 32px rgba(0,0,0,0.5)',
            fontSize:12.5, color:'var(--text)', lineHeight:1.65, whiteSpace:'pre-wrap',
          }}>
            {text}
            <div style={{fontSize:9,color:'var(--muted)',marginTop:8,textAlign:'right'}}>Click outside to close</div>
          </div>
        </>,
        document.body
      )}
    </>
  )
}

export function ColFilterDropdown({ colKey, rows, value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const options = [...new Set(rows.map(r => getVal(r[colKey])).filter(v => v !== '-'))].sort()
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  return (
    <div ref={ref} style={{position:'relative', display:'inline-block'}}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        style={{
          background: value ? 'var(--gold)' : 'transparent',
          border: 'none', cursor:'pointer', padding:'1px 4px',
          borderRadius:4, color: value ? 'var(--ink)' : 'var(--muted)',
          fontSize:10, marginLeft:4, lineHeight:1,
        }}
        title={value ? `Filtered: ${value}` : 'Filter column'}
      >
        {value ? '▼' : '⌄'}
      </button>
      {open && (
        <div style={{
          position:'absolute', zIndex:9999, background:'var(--ink2)',
          border:'0.5px solid var(--border2)', borderRadius:8,
          boxShadow:'0 8px 24px rgba(0,0,0,0.5)', minWidth:160,
          maxHeight:400, overflowY:'auto', top:'100%', left:0,
        }}>
          <div
            onClick={() => { onChange(''); setOpen(false) }}
            style={{
              padding:'8px 12px', fontSize:11.5, cursor:'pointer',
              color: !value ? 'var(--gold2)' : 'var(--muted)',
              borderBottom:'0.5px solid var(--border)',
            }}
          >All</div>
          {options.map(opt => (
            <div key={opt} onClick={() => { onChange(opt); setOpen(false) }}
              style={{
                padding:'7px 12px', fontSize:11.5, cursor:'pointer',
                color: value === opt ? 'var(--gold2)' : 'var(--text)',
                background: value === opt ? 'rgba(184,150,62,.1)' : 'transparent',
              }}
            >{opt}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function DataTable({ tableKey, columns, formFields, title, initialFilter, initialQuery, openId }) {
  const { config } = useAuth()
  const [extracting, setExtracting] = useState(false)
  const [showPanel, setShowPanel] = useState(false)

  async function handleDocUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setExtracting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }

      // Upload direct from browser to Supabase — no Vercel size limit
      const path = filePath('documents', config?.id, file.name)

      const { error: uploadError } = await supabase.storage
        .from('crew-docs')
        .upload(path, file, { contentType: 'application/pdf', upsert: false })

      if (uploadError) throw new Error('Upload failed: ' + uploadError.message)

      const publicUrl = path   // private bucket: the record keeps the PATH, opened through a signed link

      // Send just the path to extract-cert — Vercel reads it server-side (after the access check)
      const extractResp = await fetch('/api/extract-cert', {
        method: 'POST', headers,
        body: JSON.stringify({ fileUrl: publicUrl, crewName: '' }),
      })
      const extracted = await extractResp.json()
      if (extracted.error) throw new Error(extracted.error)

      setFormData({
        'Title': extracted['Title'] || '',
        'Document Type': extracted['Type'] || '',
        'Doc ID': extracted['Doc ID'] || '',
        'Issued Date': extracted['Issued Date'] || '',
        'Expiry Date': extracted['Expiry Date'] || '',
        'File URL': publicUrl || '',
        'Notes': extracted['Notes'] || '',
      })
      setModal('new')
    } catch(err) {
      console.error(err)
      alert('Failed to upload document: ' + err.message)
    } finally {
      setExtracting(false)
      e.target.value = ''
    }
  }

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState(initialQuery || '')
  const [activeFilter, setActiveFilter] = useState(initialFilter || '')
  const [colFilters, setColFilters] = useState({})
  // switching to another table page: start with that page's own filters, not the previous page's
  useEffect(() => { setActiveFilter(initialFilter || ''); setColFilters({}); setQuery(initialQuery || '') }, [tableKey])
  const [modal, setModal] = useState(null)
  // a row clicked: the record itself (read view, Edit from there); equipment also shows its maintenance tasks
  const [detail, setDetail] = useState(null)
  const [detailTasks, setDetailTasks] = useState(null)
  const [detailTodos, setDetailTodos] = useState(null)   // open To Dos with this equipment as component (sql/todo-worklists.sql)
  const [closedTodos, setClosedTodos] = useState(null)   // ... and the closed ones: the equipment's job history
  const [completionModal, setCompletionModal] = useState(null)
  const [completionData, setCompletionData] = useState({ work: '', technician: '', current_hours: '', next_date: '', next_hours: '' })
  const [formData, setFormData] = useState({})
  // the form as it opened for an edit: a save sends only what changed (cleared fields as null)
  const [origForm, setOrigForm] = useState(null)
  const [eqOpts, setEqOpts] = useState([])
  // suppliers of this vessel for a "supplier" field (inventory: preferred supplier of a critical spare)
  const [supOpts, setSupOpts] = useState([])
  useEffect(() => {
    if (!formFields.some(f => f.type === 'supplier') || !config?.id) return
    let live = true
    supabase.from('suppliers').select('id,name').eq('client_id', config.id).order('name').then(({ data }) => { if (live) setSupOpts((data || []).map(s => ({ value: s.id, label: s.name }))) })
    return () => { live = false }
  }, [formFields, config?.id])
  useEffect(() => {
    if (!formFields.some(f => f.type === 'equipment')) return
    let live = true
    fetchRows('equipment').then(rows => {
      if (!live) return
      const opts = (rows || []).map(r => ({
        value: getVal(r['UID']) || '',
        label: (getVal(r['UID']) || '') + ' — ' + (getVal(r['Name']) || ''),
      })).filter(o => o.value).sort((a, b) => a.value.localeCompare(b.value))
      setEqOpts(opts)
    }).catch(() => {})
    return () => { live = false }
  }, [])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const tableName = tableKey.replace('table_', '')
  const filters = STATUS_FILTERS[tableName] || []
  const filterableCols = FILTERABLE_COLS[tableName] || []

  const openedRef = useRef(false)
  // Equipment / Maintenance Tasks: archived rows (sql/archive.sql) are hidden; "Show archived" lists them under the active ones
  const canArchive = ['equipment', 'mtask'].includes(tableName)
  const [showArchived, setShowArchived] = useState(false)
  useEffect(() => { load() }, [showArchived]) // eslint-disable-line

  async function load() {
    setLoading(true); setError('')
    try {
      let data = await fetchRows(tableName)
      if (canArchive && showArchived) data = [...data, ...(await fetchArchived(tableName))]
      // Log tables: newest entries first (avoid scrolling to the bottom for recent work).
      if (['mlog', 'day_log', 'engine_log', 'running_log'].includes(tableName)) {
        data = [...data].reverse()
      }
      setRows(data)
      // opened from the header search: that record's window opens (once)
      if (openId && !openedRef.current) { openedRef.current = true; const hit = data.find(r => String(r.id) === String(openId)); if (hit) setDetail(hit) }
    }
    catch(e) { setError(e.message) }
    finally { setLoading(false) }
  }

  const filtered = rows.filter(r => {
    const matchQuery = !query || Object.values(r).some(v => String(v).toLowerCase().includes(query.toLowerCase()))
    const rowStatus = getVal(r['Status'])
    const matchFilter = !activeFilter || (rowStatus !== '-' && rowStatus === activeFilter)
    const matchColFilters = Object.entries(colFilters).every(([key, val]) => !val || getVal(r[key]) === val)
    return matchQuery && matchFilter && matchColFilters
  })

  function openNew() { setFormData({}); setOrigForm(null); setSaveError(''); setModal('new') }

  function openEdit(row) {
    const fd = {}
    formFields.forEach(f => {
      const v = row[f.name]
      if (v === undefined || v === null) { fd[f.name] = ''; return }
      if (typeof v === 'object' && !Array.isArray(v) && v.value) { fd[f.name] = v.value; return }
      fd[f.name] = v
    })
    setFormData(fd); setOrigForm(fd); setSaveError(''); setModal(row)
  }

  // equipment Out of Service / back in service: reason + reported by go with the save (maintenance history + diary line)
  const [statusAsk, setStatusAsk] = useState(null)
  // inventory: a changed quantity needs a reason (used / received / ...) + optional comment -> the stock history (sql/quick-wins.sql)
  const [stockAsk, setStockAsk] = useState(null)
  // the floating assistant: "this pump" / "this item" = the record open in the detail view or the edit window
  const openRec = modal && modal !== 'new' && modal.id ? modal : detail
  useEffect(() => {
    if (!openRec || !openRec.id) return
    setRecordContext({ type: title.replace(/s$/, ''), label: recordLabel(openRec, formFields), id: openRec.id, table: dbTableOf(tableName) })
    return () => clearRecordContext(openRec.id)
  }, [openRec && openRec.id]) // eslint-disable-line
  useEffect(() => {
    setDetailTasks(null); setDetailTodos(null); setClosedTodos(null)
    if (!detail || tableName !== 'equipment') return
    // To Do as the worklist: before sql/todo-worklists.sql the column is missing and the section stays hidden
    supabase.from('todo_items').select('id,text,type,department,due_date,priority').eq('equipment_id', detail.id).eq('done', false).order('priority', { ascending: false }).limit(50)
      .then(({ data, error }) => setDetailTodos(error ? null : (data || [])), () => setDetailTodos(null))
    supabase.from('todo_items').select('id,text,type,closed_at,closed_by').eq('equipment_id', detail.id).eq('done', true).order('closed_at', { ascending: false, nullsFirst: false }).limit(50)
      .then(({ data, error }) => setClosedTodos(error ? null : (data || [])), () => setClosedTodos(null))
    const uid = getVal(detail['UID'])
    let live = true
    // an archived equipment shows the tasks archived with it (its history stays readable)
    fetchRows('mtask', undefined, isArchived(detail) ? { archived: 'all' } : undefined).then(ts => { if (live) setDetailTasks((ts || []).filter(t => uid && getVal(t['Equipment']) === uid)) }).catch(() => { if (live) setDetailTasks([]) })
    return () => { live = false }
  }, [detail && detail.id]) // eslint-disable-line
  async function handleSave() {
    setSaving(true); setSaveError('')
    const body = formBody(formFields, formData, modal === 'new' ? null : origForm)
    // saved with no change: the window closes quietly, nothing is sent
    if (modal !== 'new' && !Object.keys(body).length) { setSaving(false); setModal(null); return }
    if (tableName === 'inventory' && 'Quantity' in body) {
      const row = modal === 'new' ? {} : (rows.find(r => r.id === modal.id) || {})
      const before = Number(getVal(row['Quantity'])) || 0, after = Number(body['Quantity'])
      if (body['Quantity'] !== '' && body['Quantity'] != null && after < 0) { setSaving(false); setSaveError(negativeStockMessage(formData['Part Name'] || getVal(row['Part Name']), before, after)); return }
      if (modal !== 'new') { setSaving(false); setStockAsk({ body, before, after, name: formData['Part Name'] || getVal(row['Part Name']), reason: after > before ? 'received' : 'used', comment: '', err: '' }); return }
    }
    if (tableName === 'equipment' && modal !== 'new') {
      const st = getVal((rows.find(r => r.id === modal.id) || {})['Status']), was = typeof st === 'object' ? st?.value : st
      const ask = statusReasonAsk(was, body['Status'])
      if (ask) { setSaving(false); setStatusAsk({ ...ask, body, from: was || '', to: body['Status'], reason: '', by: reportedByDefault(config), err: '' }); return }
    }
    return saveBody(body)
  }
  async function saveStock() {
    const a = stockAsk
    if (!a.reason) { setStockAsk(x => ({ ...x, err: 'Choose a reason.' })); return }
    setSaving(true)
    const { Quantity, ...rest } = a.body
    const { error } = await supabase.rpc('hc_stock_change', { p_item: modal.id, p_quantity: Number(Quantity), p_reason: a.reason, p_comment: a.comment.trim() || null })
    // before sql/quick-wins.sql: the quantity is saved with the rest, as before (no history)
    if (error && /hc_stock_change|PGRST202|does not exist/.test(error.message + ' ' + (error.code || ''))) { setStockAsk(null); return saveBody(a.body) }
    if (error) { setSaving(false); setStockAsk(x => ({ ...x, err: error.message })); return }
    setStockAsk(null)
    if (Object.keys(rest).length) return saveBody(rest)
    setSaving(false); setModal(null); await load()
  }
  async function saveBody(body) {
    setSaving(true); setSaveError('')
    // Check if marking mtask as Completed
    if (tableName === 'mtask' && modal !== 'new' && body['Status'] === 'Completed') {
      setSaving(false)
      const fullRow = rows.find(r => r.id === modal.id) || {}
      const freqDays = parseInt(getVal(fullRow['Frequency Days']) || 0)
      const freqHours = parseInt(getVal(fullRow['Frequency Hours']) || 0)
      console.log('freqDays:', freqDays, 'freqHours:', freqHours, 'fullRow keys:', Object.keys(fullRow))
      const today = new Date()
      const nextDate = freqDays ? new Date(today.getTime() + freqDays * 86400000).toISOString().split('T')[0] : ''
      setCompletionData({ work: '', technician: '', current_hours: '', next_date: nextDate, next_hours: '', freqHours })
      const currentHours = formData['_currentHours'] || ''
      setCompletionModal({ id: modal.id, body, form: { ...formData, ...body }, row: fullRow, currentHours })
      setModal(null)
      return
    }
    try {
      if (modal === 'new') {
        const newRow = await createRow(tableName, body)
        setRows(prev => [newRow, ...prev])
      } else {
        const updated = await updateRow(tableName, modal.id, body)
        setRows(prev => prev.map(r => r.id === modal.id ? updated : r))
      }
      setModal(null)
    } catch(e) { setSaveError('Save failed: ' + e.message) }
    finally { setSaving(false) }
  }

  async function handleCompletion() {
    setSaving(true); setSaveError('')
    try {
      const body = { ...completionModal.body }
      // the save sends only changed fields; name / frequency / equipment come from the whole form
      const all = { ...(completionModal.form || {}), ...body }
      // Calculate next service date from frequency days
      const freqDays = parseInt(all['Frequency Days'] || 0)
      if (completionData.next_date) body['Next Service Date'] = completionData.next_date
      else if (freqDays) {
        const d = new Date()
        d.setDate(d.getDate() + freqDays)
        body['Next Service Date'] = d.toISOString().split('T')[0]
      }
      // Calculate next service hours from current hours + frequency hours
      const freqHours = parseInt(all['Frequency Hours'] || 0)
      const currentHours = parseFloat(completionModal.currentHours || completionData.current_hours || 0)
      if (currentHours && freqHours) {
        body['Next Service Hours'] = currentHours + freqHours
      }
      await updateRow(tableName, completionModal.id, body)
      // Update equipment current hours
      if (currentHours && all['Equipment']) {
        const eqUID = Array.isArray(all['Equipment']) ? all['Equipment'][0]?.value : all['Equipment']
        if (eqUID) {
          const eqRows = await fetchRows('equipment')
          const eq = eqRows.find(r => getVal(r['UID']) === eqUID || getVal(r['Name']) === eqUID)
          if (eq) await updateRow('equipment', eq.id, { 'Current Hours': currentHours })
        }
      }
      // Create maintenance log entry
      const logBody = {
        'Task': all['Name'] || all['Task Name'] || '',
        'Work Description': completionData.work,
        'Technician': completionData.technician,
        'Date Completed': new Date().toISOString().split('T')[0],
        'Hours at Service': currentHours || '',
      }
      await createRow('mlog', logBody)
      setRows(prev => prev.map(r => r.id === completionModal.id ? { ...r, ...body } : r))
      setCompletionModal(null)
    } catch(e) { setSaveError('Failed: ' + e.message) }
    finally { setSaving(false) }
  }

  async function handleArchive(row) {
    const name = recordName(row, formFields) || 'this record'
    const extra = tableName === 'equipment' ? ' Its active maintenance tasks are archived with it.' : ''
    const reason = window.prompt('Archive "' + name + '"? It is hidden from the lists, counts, e-mails and the AI, and kept with its history (Show archived → Unarchive).' + extra + '\n\nReason (optional):', '')
    if (reason === null) return
    try { await archiveRow(tableName, row.id, reason.trim()); setDetail(null); load() }
    catch (e) { alert('Could not archive: ' + e.message + (archiveReady() ? '' : ' (run sql/archive.sql first)')) }
  }
  async function handleUnarchive(row) {
    try { await unarchiveRow(tableName, row.id); setDetail(null); load() }
    catch (e) { alert('Could not unarchive: ' + e.message) }
  }

  async function handleDelete(row) {
    const name = recordName(row)   // the title / name column, never created_at or an id
    const dbt = dbTableOf(tableName)
    const undoable = dbt && dbt !== 'crew'   // crew: select=* refused, so nothing to put back
    if (!window.confirm('Delete "' + name + '"?' + (undoable ? ' You can undo it for 10 seconds.' : ' This cannot be undone.'))) return
    try {
      if (undoable) await deleteWithUndo(supabase, { table: dbt, id: row.id, label: name === 'this record' ? 'record' : name, onUndone: () => load() })
      else await deleteRow(tableName, row.id)
      setRows(prev => prev.filter(r => r.id !== row.id))
    }
    catch(e) { alert('Delete failed: ' + e.message) }
  }

  const activeColFilters = Object.values(colFilters).filter(Boolean).length

  return (
    <>
      <style>{`
        @media (max-width: 1023px) {
          .tbl-toolbar {
            flex-wrap: wrap !important;
            gap: 8px !important;
            padding: 10px !important;
          }
          .tbl-toolbar > div:first-child { flex: 1 1 100% !important; display: block !important; }
          .search-inp { width: 100% !important; box-sizing: border-box !important; }
          .tbl-toolbar > .btn { flex: 1 1 auto !important; min-width: 88px !important; font-size: 12px !important; padding: 8px 10px !important; }
          .tbl-wrap { overflow-x: auto !important; }
          .tbl-wrap table { min-width: 640px; }
        }
        /* iPad (portrait and landscape, with or without the sidebar): the table fits the width - cells wrap instead of
           the table scrolling sideways (ipad) */
        @media (min-width: 600px) and (max-width: 1279px) {
          .tbl-wrap { overflow-x: visible !important; }
          .tbl-wrap table { min-width: 0 !important; }
          .tbl-wrap th, .tbl-wrap td { white-space: normal !important; overflow-wrap: break-word; padding: 9px 6px !important; max-width: none !important; }
          .tbl-wrap td { font-size: 12px !important; }
          .tbl-wrap th { font-size: 9.5px !important; letter-spacing: .02em !important; }
        }
        @media (min-width: 600px) and (max-width: 800px) {
          .tbl-wrap th, .tbl-wrap td { padding: 8px 4px !important; }
        }
      `}</style>
      <div className="tbl-wrap fade-up">
        <div className="tbl-toolbar">
          <div style={{position:'relative',display:'inline-block'}}>
            <input className="search-inp" placeholder={'Search ' + title.toLowerCase() + '...'} value={query} onChange={e => setQuery(e.target.value)} />
            {query && <button onClick={() => setQuery('')} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:14,lineHeight:1}}>✕</button>}
          </div>
          {activeColFilters > 0 && (
            <button className="btn" onClick={() => setColFilters({})} style={{fontSize:11, color:'var(--gold2)'}}>
              Clear filters ({activeColFilters})
            </button>
          )}
          <button className="btn" onClick={() => { setQuery(''); load(); }} style={{fontSize:11}}>Refresh</button>
          {canArchive && (
            <button className="btn" onClick={() => setShowArchived(v => !v)} style={{fontSize:11, color: showArchived ? 'var(--gold2)' : undefined}} title="Archived rows are kept with their history">
              {showArchived ? 'Hide archived (' + rows.filter(isArchived).length + ')' : 'Show archived'}
            </button>
          )}
          {tableKey === 'docs' && (
            <button className="btn" onClick={() => setShowPanel(p => !p)} style={{fontSize:11, color: showPanel ? 'var(--gold2)' : undefined}}>Categories</button>
          )}
          {tableKey === 'docs' && (
            <label style={{ cursor: extracting ? 'wait' : 'pointer' }}>
              <input type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleDocUpload} disabled={extracting} />
              <span className="btn btn-primary" style={{ fontSize: 11 }}>{extracting ? 'Reading...' : 'Upload Document'}</span>
            </label>
          )}
          <ExportMenu get={() => ({ title, rows: filtered, columns })} />
          <button className="btn btn-primary" onClick={openNew}>+ Add</button>
        </div>

        {filters.length > 0 && (
          <div className="filter-bar">
            <button className={'filter-chip' + (activeFilter === '' ? ' active' : '')} onClick={() => setActiveFilter('')}>
              All <span className="filter-count">{rows.length}</span>
            </button>
            {filters.map(f => (
              <button key={f} className={'filter-chip' + (activeFilter === f ? ' active' : '')} onClick={() => setActiveFilter(activeFilter===f?'':f)}>
                {f} <span className="filter-count">{rows.filter(r => getVal(r['Status'])===f).length}</span>
              </button>
            ))}
          </div>
        )}

        {loading && <div className="loading-row"><div className="spin" />Loading...</div>}
        {error && <div className="err-box" style={{margin:12}}>{error}</div>}

        {!loading && !error && (
          <div style={{overflowX:'auto', overflowY:'visible', paddingBottom:260}}>
            <table>
              <thead><tr>
                {columns.map(c => (
                  <th key={c.key}>
                    <div style={{display:'flex', alignItems:'center', gap:2}}>
                      {c.label}
                      {filterableCols.includes(c.key) && (
                        <ColFilterDropdown
                          colKey={c.key}
                          rows={rows}
                          value={colFilters[c.key] || ''}
                          onChange={val => setColFilters(prev => ({...prev, [c.key]: val}))}
                        />
                      )}
                    </div>
                  </th>
                ))}
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0
                  ? <tr><td colSpan={columns.length+1}><div className="no-rows">
                      {activeFilter ? 'No '+activeFilter+' records.' : 'No records found.'}
                    </div></td></tr>
                  : filtered.map(row => (
                    <tr key={row.id} className="data-row" style={{cursor:'pointer', opacity: isArchived(row) ? 0.55 : 1}} title={archivedLine(row) || undefined} onClick={e => { if (e.target.closest('button, a, input')) return; setDetail(row) }} tabIndex={0} onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDetail(row) } }}>
                      {columns.map(c => {
                        const display = /hours/i.test(c.key) ? fmtNum(getVal(row[c.key])) : fmtDate(getVal(row[c.key]))
                        const autoStatus = c.key === 'Status' ? (isArchived(row) ? 'Archived' : calcStatus(row, tableName)) : null
                        const display2 = autoStatus || display
                        const pillCls = PILL_MAP[display2] || PILL_MAP[display]
                        return (
                          <td key={c.key} title={c.comment ? '' : display}>
                            {c.comment
                              ? <CommentBubble text={display === '-' ? null : display} />
                              : c.pill && (PILL_MAP[display2] || pillCls)
                                ? <span className={'pill '+(PILL_MAP[display2] || pillCls)}>{display2}</span>
                                : display
                            }
                          </td>
                        )
                      })}
                      <td>
                        <div style={{display:'flex',gap:5}}>
                          {isArchived(row)
                            ? <button className="act-btn" onClick={() => handleUnarchive(row)}>Unarchive</button>
                            : <><button className="act-btn" onClick={() => openEdit(row)}>Edit</button>
                              <button className="act-btn del" onClick={() => handleDelete(row)}>Del</button></>}
                          {row['File URL'] && <a href={fileHref(row['File URL'])} target="_blank" rel="noreferrer" className="act-btn" style={{textDecoration:'none'}}>📎</a>}
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <div className="tbl-footer">
            {filtered.length} of {rows.length} records
            {activeFilter && <span style={{color:'var(--gold2)'}}> — {activeFilter}</span>}
            {activeColFilters > 0 && <span style={{color:'var(--gold2)'}}> — {activeColFilters} column filter{activeColFilters>1?'s':''} active</span>}
          </div>
        )}
      </div>

      {completionModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(10,22,40,.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000}}>
          <div style={{background:'var(--hull)',color:'var(--text)',borderRadius:12,padding:24,width:'min(500px,95vw)',border:'1px solid var(--border2)'}}>
            <h3 style={{margin:'0 0 16px',fontSize:16,color:'var(--text)'}}>✅ Task Completed — Log Entry</h3>
            <p style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>A Maintenance Log entry will be created automatically.</p>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,letterSpacing:'.03em',color:'var(--muted)'}}>Work Performed *</label>
                <textarea style={compInp} rows={3} value={completionData.work} onChange={e => setCompletionData(d => ({...d, work: e.target.value}))} placeholder="Describe the work performed..." />
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,letterSpacing:'.03em',color:'var(--muted)'}}>Technician *</label>
                <input style={compInp} value={completionData.technician} onChange={e => setCompletionData(d => ({...d, technician: e.target.value}))} placeholder="Name of technician" />
              </div>

            </div>
            {saveError && <div style={{color:'#b4313c',fontSize:12,marginTop:8}}>{saveError}</div>}
            <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:16}}>
              <button style={{padding:'8px 16px',borderRadius:6,border:'1px solid var(--border2)',background:'var(--sea)',color:'var(--text)',cursor:'pointer',fontSize:13}} onClick={() => setCompletionModal(null)}>Cancel</button>
              <button style={{padding:'8px 16px',borderRadius:6,border:'none',background:'var(--gold)',color:'#fff',fontWeight:600,cursor:'pointer',fontSize:13,opacity:(saving || !completionData.work || !completionData.technician)?.5:1}} onClick={handleCompletion} disabled={saving || !completionData.work || !completionData.technician}>{saving ? 'Saving...' : 'Save & Log'}</button>
            </div>
          </div>
        </div>
      )}

      {statusAsk && (
        <Modal title={statusAsk.to === 'Out of Service' ? 'Out of Service - reason' : 'Back in service'} onClose={() => setStatusAsk(null)} saving={saving} error={statusAsk.err}
          onSave={async () => {
            if (statusAsk.required && !statusAsk.reason.trim()) { setStatusAsk(a => ({ ...a, err: 'A reason is needed to take equipment out of service.' })); return }
            const body = { ...statusAsk.body, ...statusReasonFields(statusAsk.reason, statusAsk.by) }
            setStatusAsk(null); await saveBody(body)
          }}>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 10 }}>{(statusAsk.from || '\u2013') + ' \u2192 ' + statusAsk.to}. Written to the maintenance history and the ship's diary.</div>
          <div className="field"><label>Reason{statusAsk.required ? ' *' : ' (optional)'}</label>
            <textarea autoFocus rows={3} value={statusAsk.reason} placeholder={statusAsk.required ? 'e.g. Raw water pump leaking' : 'e.g. Impeller replaced'} onChange={e => setStatusAsk(a => ({ ...a, reason: e.target.value, err: '' }))} /></div>
          <div className="field"><label>Reported by</label>
            <input value={statusAsk.by} onChange={e => setStatusAsk(a => ({ ...a, by: e.target.value }))} /></div>
        </Modal>
      )}

      {stockAsk && (
        <Modal title="Stock change - reason" onClose={() => setStockAsk(null)} saving={saving} error={stockAsk.err} onSave={saveStock}>
          <div style={{ fontSize: 13, marginBottom: 10 }}><b>{stockAsk.name || 'Item'}</b>: {fmtQty(stockAsk.before)} {'\u2192'} {fmtQty(stockAsk.after)} ({stockAsk.after - stockAsk.before > 0 ? '+' : ''}{fmtQty(stockAsk.after - stockAsk.before)}). Saved to the item's stock history.</div>
          <div className="field"><label>Reason *</label>
            <select autoFocus value={stockAsk.reason} onChange={e => setStockAsk(a => ({ ...a, reason: e.target.value, err: '' }))}>
              <option value="">Choose{'\u2026'}</option>
              {STOCK_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          <div className="field"><label>Comment (optional)</label>
            <textarea rows={2} value={stockAsk.comment} maxLength={500} placeholder="e.g. 2 filters for the port generator service" onChange={e => setStockAsk(a => ({ ...a, comment: e.target.value }))} /></div>
        </Modal>
      )}

      {detail && !modal && (
        <Modal title={recordLabel(detail, formFields) || title.replace(/s$/, '')} onClose={() => setDetail(null)}
          footer={<><button className="btn" onClick={() => setDetail(null)}>Close</button>
            {canArchive && (isArchived(detail)
              ? <button className="btn btn-primary" onClick={() => handleUnarchive(detail)}>Unarchive</button>
              : <><button className="btn" onClick={() => handleArchive(detail)}>Archive</button><button className="btn btn-primary" onClick={() => { const r = detail; setDetail(null); openEdit(r) }}>Edit</button></>)}
            {!canArchive && <button className="btn btn-primary" onClick={() => { const r = detail; setDetail(null); openEdit(r) }}>Edit</button>}</>}>
          <div className="hc-record-view" data-record-id={detail.id}>
            {isArchived(detail) && <div style={{ fontSize: 12.5, color: 'var(--gold2)', marginBottom: 8 }}>{archivedLine(detail)}. Kept with its history; not in lists, counts, e-mails or the AI.</div>}
            <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 6 }}><HistoryLink table={dbTableOf(tableName)} rowId={detail.id} /></div>
            {[...new Set([...columns.map(c => c.key), ...formFields.map(f => f.name)])].map(k => {
              const v = getVal(detail[k]), shown = v && typeof v === 'object' ? v.value : v
              if (shown == null || shown === '' || shown === '-') return null
              return <div key={k} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
                <span style={{ width: 140, flexShrink: 0, color: 'var(--muted)' }}>{(formFields.find(f => f.name === k) || {}).label || k}</span>
                <span style={{ color: 'var(--text)', whiteSpace: 'pre-wrap', minWidth: 0, overflowWrap: 'anywhere' }}>{/hours/i.test(k) ? fmtNum(shown) : fmtDate(shown)}</span>
              </div>
            })}
            {(tableName === 'equipment' || tableName === 'mtask') && getVal(detail[tableName === 'equipment' ? 'UID' : 'Plan ID']) && (
              <LinkedManuals kind={tableName === 'equipment' ? 'equipment' : 'maintenance_task'} targetId={getVal(detail[tableName === 'equipment' ? 'UID' : 'Plan ID'])} title={recordLabel(detail, formFields) || ''} />
            )}
            {tableName === 'equipment' && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>Maintenance tasks</div>
                {detailTasks == null ? <div className="loading-row"><div className="spin" />Loading...</div>
                  : !detailTasks.length ? <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No maintenance tasks for this equipment.</div>
                  : detailTasks.map(t => <div key={t.id} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '4px 0' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>{getVal(t['Task Name'])}</span>
                      <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{['Next Service Date', 'Next Service Hours'].map(k => getVal(t[k])).filter(x => x && x !== '-').map((x, i) => i ? fmtNum(x) + ' h' : fmtDate(x)).join(' / ') || '-'}</span>
                      <span className={'pill ' + (PILL_MAP[getVal(t['Status'])] || '')}>{getVal(t['Status'])}</span>
                    </div>)}
                {detailTodos && <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', margin: '14px 0 6px' }}>Open To Dos on this equipment</div>
                  {!detailTodos.length ? <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No open To Dos.</div>
                    : detailTodos.map(t => <div key={t.id} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }} title="Open in To Do"
                        onClick={() => { try { localStorage.setItem('hc_open_todo', String(t.id)) } catch (e) {} setDetail(null); window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: 'todo' } })) }}>
                        <span style={{ flex: 1, minWidth: 0 }}>{t.text}</span>
                        <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{[t.type && t.type !== 'General' ? t.type : '', t.department, t.due_date ? fmtDate(t.due_date) : ''].filter(Boolean).join(' \u00b7 ')}</span>
                        <span className="pill pill-r">Open</span>
                      </div>)}
                </>}
                {closedTodos && closedTodos.length > 0 && <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', margin: '14px 0 6px' }}>Closed To Dos (history)</div>
                  {closedTodos.map(t => <div key={t.id} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }} title="Open in To Do"
                      onClick={() => { try { localStorage.setItem('hc_open_todo', String(t.id)) } catch (e) {} setDetail(null); window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: 'todo' } })) }}>
                      <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap', width: 92, flexShrink: 0 }}>{t.closed_at ? fmtDate(String(t.closed_at).slice(0, 10)) : '-'}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>{t.text}</span>
                      <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{[t.type && t.type !== 'General' ? t.type : '', t.closed_by].filter(Boolean).join(' \u00b7 ')}</span>
                      <span className="pill pill-g">Closed</span>
                    </div>)}
                </>}
              </div>
            )}
          </div>
        </Modal>
      )}

      {modal && !statusAsk && !stockAsk && (
        <Modal title={modal==='new' ? 'Add '+title.replace(/s$/,'') : 'Edit record'} onClose={() => setModal(null)} onSave={handleSave} saving={saving} error={saveError}>
          {modal !== 'new' && modal?.id && <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 6 }}><HistoryLink table={dbTableOf(tableName)} rowId={modal.id} /></div>}
          {tableName === 'inventory' && modal !== 'new' && modal?.id && <StockHistory itemId={modal.id} />}
          {formFields.map(f => (
            f.name === 'Status' && tableName === 'mtask' ? (
              <div key={f.name} className="field">
                <label>{f.label} *</label>
                <select value={formData[f.name]||''} onChange={e => {
                  const val = e.target.value
                  setFormData(p => {
                    const updated = {...p, [f.name]: val}
                    if (val === 'Completed') {
                      const fullRow = rows.find(r => r.id === modal?.id) || {}
                      const freqDays = parseInt(getVal(fullRow['Frequency Days']) || 0)
                      const freqHours = parseInt(getVal(fullRow['Frequency Hours']) || 0)
                      const today = new Date()
                      const nextDate = freqDays ? new Date(today.getTime() + freqDays * 86400000).toISOString().split('T')[0] : ''
                      updated['_nextDate'] = nextDate
                      updated['_freqHours'] = freqHours
                      updated['Next Service Date'] = nextDate
                      updated['Next Service Hours'] = ''
                    }
                    return updated
                  })
                }}>
                  <option value="">Select...</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                {formData[f.name] === 'Completed' && (
                  <div style={{marginTop:12,padding:12,background:'var(--surface2)',borderRadius:8,border:'1px solid var(--border)'}}>
                    <div style={{fontSize:11,color:'var(--gold3)',marginBottom:8,fontWeight:600}}>✅ Completion Details</div>
                    <div className="field" style={{marginBottom:8}}>
                      <label>Current Hours (updates equipment)</label>
                      <input type="number" placeholder="e.g. 6080" value={formData['_currentHours']||''} onChange={e => {
                        const h = parseFloat(e.target.value) || 0
                        const fh = formData['_freqHours'] || 0
                        setFormData(p => ({...p, '_currentHours': e.target.value, 'Next Service Hours': h && fh ? h + fh : p['Next Service Hours']}))
                      }} />
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <div className="field">
                        <label>Next Service Date</label>
                        <DateField value={formData['Next Service Date'] || formData['_nextDate'] || ''} onChange={v => setFormData(p => ({...p,'Next Service Date':v, '_nextDate': v}))} />
                      </div>
                      <div className="field">
                        <label>Next Service Hours</label>
                        <input type="number" placeholder="Auto-calculated" value={formData['Next Service Hours']||''} onChange={e => setFormData(p => ({...p,'Next Service Hours':e.target.value}))} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) :
            <div key={f.name} className="field">
              <label>{f.label||f.name}{f.required?' *':''}</label>
              {f.type==='supplier'
                ? <select value={formData[f.name]||''} onChange={e => setFormData(p => ({...p,[f.name]:e.target.value}))}>
                    <option value="">— None —</option>
                    {supOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                : f.type==='yesno'
                ? <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={formData[f.name] === true || formData[f.name] === 'true'} onChange={e => setFormData(p => ({...p,[f.name]:e.target.checked}))} />{f.hint || 'Yes'}</label>
                : f.type==='equipment'
                ? <select value={formData[f.name]||''} onChange={e => setFormData(p => ({...p,[f.name]:e.target.value}))}>
                    <option value="">— None —</option>
                    {eqOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                : f.type==='select'
                ? <select value={formData[f.name]||''} onChange={e => setFormData(p => ({...p,[f.name]:e.target.value}))}>
                    <option value="">Select...</option>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                : f.type==='textarea'
                ? <textarea rows={3} placeholder={f.placeholder||''} value={formData[f.name]||''} onChange={e => setFormData(p => ({...p,[f.name]:e.target.value}))} />
                : f.type==='url'
                ? <><div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <input type="text" placeholder={f.placeholder||''} value={formData[f.name]||''} onChange={e => setFormData(p => ({...p,[f.name]:e.target.value}))} style={{flex:1}} />
                    <label style={{cursor:'pointer',whiteSpace:'nowrap'}}>
                      <input type="file" accept="application/pdf" style={{display:'none'}} onChange={async ev => {
                        const file = ev.target.files[0]; if (!file) return
                        const { data: { session } } = await supabase.auth.getSession()
                        let path; try { path = filePath('documents', config?.id, file.name) } catch (e2) { alert(e2.message); return }
                        const { error } = await supabase.storage.from('crew-docs').upload(path, file, { contentType: 'application/pdf', upsert: false })
                        if (error) { alert('Upload failed: ' + error.message); return }
                        setFormData(p => ({...p,[f.name]:path}))   // private bucket: the PATH, opened through a signed link
                        ev.target.value = ''
                      }} />
                      <span className="btn" style={{fontSize:11}}>📎 Upload</span>
                    </label>
                  </div>
                  {formData[f.name] && <a href={fileHref(formData[f.name])} target="_blank" rel="noreferrer" style={{fontSize:11,color:'var(--gold2)',display:'block',marginTop:4}}>📎 View Document</a>}</>
                : f.type==='date'
                ? <DateField value={formData[f.name]||''} onChange={v => setFormData(p => ({...p,[f.name]:v}))} />
                : <input type={f.type||'text'} placeholder={f.placeholder||''} value={formData[f.name]||''} onChange={e => setFormData(p => ({...p,[f.name]:e.target.value}))} />
              }
            </div>
          ))}
        </Modal>
      )}
      {showPanel && tableKey === 'docs' && (() => {
        const typeCounts = {}
        rows.forEach(r => {
          const t = getVal(r['Document Type'])
          if (t && t !== '-') typeCounts[t] = (typeCounts[t] || 0) + 1
        })
        const types = Object.entries(typeCounts).sort((a,b) => b[1]-a[1])
        return (
          <div style={{
            position:'fixed', top:0, right:0, width:260, height:'100vh', zIndex:1000,
            background:'var(--hull)', borderLeft:'0.5px solid var(--border)',
            boxShadow:'-8px 0 32px rgba(0,0,0,0.4)', display:'flex', flexDirection:'column',
          }}>
            <div style={{padding:'20px 16px 12px', borderBottom:'0.5px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <span style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>Categories</span>
              <button onClick={() => setShowPanel(false)} style={{background:'transparent',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:16}}>×</button>
            </div>
            <div style={{padding:'8px 10px', overflowY:'auto', flex:1}}>
              <button
                onClick={() => { setColFilters({}); }}
                style={{
                  width:'100%', textAlign:'left', background: !colFilters['Document Type'] ? 'rgba(184,150,62,.12)' : 'transparent',
                  border:'none', borderRadius:8, padding:'10px 12px', cursor:'pointer',
                  color: !colFilters['Document Type'] ? 'var(--gold2)' : 'var(--text)',
                  fontSize:12, display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2,
                }}
              >
                <span>All Documents</span>
                <span style={{background:'rgba(255,255,255,.08)',borderRadius:10,padding:'1px 7px',fontSize:10}}>{rows.length}</span>
              </button>
              {types.map(([type, count]) => (
                <button
                  key={type}
                  onClick={() => setColFilters(p => ({...p, 'Document Type': p['Document Type'] === type ? '' : type}))}
                  style={{
                    width:'100%', textAlign:'left', border:'none', borderRadius:8, padding:'10px 12px', cursor:'pointer',
                    background: colFilters['Document Type'] === type ? 'rgba(184,150,62,.12)' : 'transparent',
                    color: colFilters['Document Type'] === type ? 'var(--gold2)' : 'var(--text)',
                    fontSize:12, display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2,
                  }}
                >
                  <span>{type}</span>
                  <span style={{background:'rgba(255,255,255,.08)',borderRadius:10,padding:'1px 7px',fontSize:10}}>{count}</span>
                </button>
              ))}
            </div>
          </div>
        )
      })()}
    </>
  )
}

const cs = {
  commentBtn: {
    background:'transparent', border:'0.5px solid var(--border)',
    color:'var(--muted)', cursor:'pointer', padding:'3px 6px',
    borderRadius:5, display:'inline-flex', alignItems:'center', transition:'all .13s',
  },
}

const s = {
  filterBar: { display:'flex', flexWrap:'wrap', gap:6, padding:'10px 14px', borderBottom:'0.5px solid var(--border)', background:'var(--hull)' },
  filterChip: { display:'flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:20, border:'0.5px solid var(--border)', background:'transparent', color:'var(--muted)', fontSize:11, cursor:'pointer', fontFamily:'inherit', transition:'all .13s' },
  filterActive: { background:'var(--chip)', color:'var(--gold2)', borderColor:'rgba(184,150,62,.35)' },
  filterCount: { background:'rgba(255,255,255,.08)', borderRadius:10, padding:'0 5px', fontSize:10 },
  tableFooter: { padding:'8px 14px', fontSize:10, color:'var(--muted)', borderTop:'0.5px solid var(--border)', background:'var(--hull)' },
}

// Inventory: the item's stock history (who, when, before -> after, reason, comment) - sql/quick-wins.sql
function StockHistory({ itemId }) {
  const [open, setOpen] = useState(false), [rows, setRows] = useState(null), [err, setErr] = useState('')
  useEffect(() => {
    if (!open || rows) return
    supabase.from('inventory_stock_history').select('*').eq('inventory_id', itemId).order('created_at', { ascending: false }).limit(30)
      .then(({ data, error }) => { if (error) setErr(/inventory_stock_history/.test(error.message) ? 'Stock history starts once sql/quick-wins.sql has been run.' : error.message); else setRows(data || []) })
  }, [open, rows, itemId])
  return (
    <div style={{ marginBottom: 8, fontSize: 12.5 }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', color: 'var(--gold2)', cursor: 'pointer', padding: 0, fontSize: 12.5 }}>{open ? '\u25be' : '\u25b8'} Stock history</button>
      {open && (err ? <div style={{ color: 'var(--muted)', marginTop: 4 }}>{err}</div> : !rows ? <div style={{ color: 'var(--muted)', marginTop: 4 }}>Loading{'\u2026'}</div>
        : !rows.length ? <div style={{ color: 'var(--muted)', marginTop: 4 }}>No stock changes yet.</div>
        : <div style={{ marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>{rows.map((h, i) => (
            <div key={i} style={{ padding: '3px 0', borderBottom: '0.5px solid var(--border)' }}>
              {new Date(h.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} {'\u00b7'} {h.changed_by || 'Unknown'}{h.source === 'ai' ? ' (AI)' : ''} {'\u00b7'} {fmtQty(h.qty_before)} {'\u2192'} {fmtQty(h.qty_after)} {'\u00b7'} <b>{STOCK_REASON_LABEL[h.reason] || h.reason}</b>{h.comment ? ' \u2014 ' + h.comment : ''}{h.offline_at ? ' \u00b7 made offline ' + new Date(h.offline_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
            </div>))}</div>)}
    </div>
  )
}

// the name of a row for the assistant ("Port Generator", "AHU filter panel"): its name-like field, else the first filled one
export function recordLabel(row, formFields) {
  const val = k => { const v = getVal(row && row[k]); return v && v !== '-' ? String(typeof v === 'object' ? (v.value || '') : v).trim() : '' }
  for (const k of ['Name', 'Part Name', 'Task', 'Task Name', 'Title', 'Document', 'Charter Ref', 'Port', 'Marina Name', 'Equipment']) { const v = val(k); if (v) return v }
  for (const f of formFields || []) { const v = val(f.name); if (v) return v }
  return ''
}
